import React from 'react';
import { CheckCircle, Edit, Trash2, Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const ReminderTable = ({ reminders, onMarkComplete, onEdit, onDelete, onView, sortConfig, onSort }) => {
  const getSortIcon = (columnKey) => {
    if (sortConfig?.key !== columnKey) {
      return <ArrowUpDown size={14} style={{ marginLeft: '4px', opacity: 0.3 }} />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} style={{ marginLeft: '4px' }} />
      : <ArrowDown size={14} style={{ marginLeft: '4px' }} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (isCompleted, isDelete) => {
    if (isDelete) {
      return <span className="badge" style={{ background: '#ef4444', color: '#fff', fontSize: '14px', padding: '4px 10px', borderRadius: '16px' }}>Deleted</span>;
    }
    if (isCompleted) {
      return <span className="badge" style={{ background: '#6b7280', color: '#fff', fontSize: '14px', padding: '4px 10px', borderRadius: '16px' }}>Inactive</span>;
    }
    return <span className="badge" style={{ background: '#10b981', color: '#fff', fontSize: '14px', padding: '4px 10px', borderRadius: '16px' }}>Active</span>;
  };

  const getDueText = (daysUntil, isOverdue, isCompleted) => {
    if (isCompleted) {
      return { text: 'Completed', color: '#10b981' };
    }
    if (isOverdue) {
      const daysOverdue = Math.abs(daysUntil);
      return { text: `Overdue by ${daysOverdue} day${daysOverdue !== 1 ? 's' : ''}`, color: '#ef4444' };
    }
    if (daysUntil === 0) {
      return { text: 'Due Today', color: '#f59e0b' };
    }
    if (daysUntil === 1) {
      return { text: 'Due Tomorrow', color: '#f59e0b' };
    }
    if (daysUntil <= 7) {
      return { text: `Due in ${daysUntil} days`, color: '#eab308' };
    }
    return { text: `Due in ${daysUntil} days`, color: '#3b82f6' };
  };

  const getFrequencyText = (frequency, recurrencePattern) => {
    if (frequency !== 'none') return frequency;
    if (recurrencePattern?.type === 'monthly') {
      return `Monthly on ${recurrencePattern.dayOfMonth}${getOrdinalSuffix(recurrencePattern.dayOfMonth)}`;
    }
    if (recurrencePattern?.type === 'weekly') {
      return 'Weekly';
    }
    if (recurrencePattern?.type === 'yearly') {
      return 'Yearly';
    }
    return 'One-time';
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return (
    <div className="items-table-container">
      <table className="table items-table">
        <thead>
          <tr>
            <th 
              onClick={() => onSort('description')} 
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Description
                {getSortIcon('description')}
              </div>
            </th>
            <th 
              onClick={() => onSort('relatedItemName')} 
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Related Item
                {getSortIcon('relatedItemName')}
              </div>
            </th>
            <th 
              onClick={() => onSort('referenceType')} 
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Reference
                {getSortIcon('referenceType')}
              </div>
            </th>
            <th 
              onClick={() => onSort('type')} 
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Type
                {getSortIcon('type')}
              </div>
            </th>
            <th 
              onClick={() => onSort('date')} 
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Due Date
                {getSortIcon('date')}
              </div>
            </th>
            <th 
              onClick={() => onSort('isOverdue')} 
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Status
                {getSortIcon('isOverdue')}
              </div>
            </th>
            <th>Frequency</th>
            <th 
              onClick={() => onSort('createdAt')} 
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Created
                {getSortIcon('createdAt')}
              </div>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reminders.map((reminder) => {
            const dueInfo = getDueText(reminder.daysUntil, reminder.isOverdue, reminder.isCompleted);
            
            return (
              <tr key={reminder.id || reminder._id}>
                <td>
                  <div>
                    <div style={{ fontWeight: '600', color: '#2b3674', fontSize: '14px', marginBottom: '4px' }}>
                      {reminder.description || '-'}
                    </div>
                    <div style={{ fontSize: '12px', color: dueInfo.color, fontWeight: '500' }}>
                      {dueInfo.text}
                    </div>
                  </div>
                </td>
                <td>
                  {reminder.relatedItemName ? (
                    <div>
                      <div style={{ fontWeight: '600', color: '#2b3674', fontSize: '14px' }}>
                        {reminder.relatedItemName}
                      </div>
                      {reminder.relatedItemType && (
                        <div style={{ fontSize: '12px', color: '#707EAE', textTransform: 'capitalize' }}>
                          {reminder.relatedItemType}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                  )}
                </td>
                <td>
                  <span className="badge" style={{ 
                    background: reminder.referenceType === 'asset' ? '#e3f2fd' : '#fff3e0',
                    color: reminder.referenceType === 'asset' ? '#1976d2' : '#f57c00',
                    fontSize: '14px',
                    padding: '4px 10px',
                    borderRadius: '16px',
                    textTransform: 'capitalize'
                  }}>
                    {reminder.referenceType || '-'}
                  </span>
                </td>
                <td>
                  <span style={{ textTransform: 'capitalize', color: '#707EAE', fontSize: '14px', fontWeight: '500' }}>
                    {reminder.type || '-'}
                  </span>
                </td>
                <td style={{ color: '#2b3674', fontWeight: '500', fontSize: '14px' }}>
                  {formatDate(reminder.date)}
                </td>
                <td>{getStatusBadge(reminder.isCompleted, reminder.isDelete)}</td>
                <td style={{ color: '#707EAE', fontSize: '14px', fontWeight: '500' }}>
                  {getFrequencyText(reminder.frequency, reminder.recurrencePattern)}
                </td>
                <td>
                  <span style={{ fontSize: '14px', color: '#707EAE' }}>{formatDate(reminder.createdAt)}</span>
                </td>
                <td>
                  <div className="table-actions">
                    {onView && (
                      <button className="action-icon view-btn" onClick={() => onView(reminder)} title="View">
                        <Eye size={14} />
                      </button>
                    )}
                    {onEdit && !reminder.isCompleted && (
                      <button className="action-icon edit-btn" onClick={() => onEdit(reminder)} title="Edit">
                        <Edit size={14} />
                      </button>
                    )}
                    {onMarkComplete && !reminder.isCompleted && (
                      <button 
                        className="action-icon" 
                        style={{ background: '#d5f3e9', color: '#0c9f6e' }}
                        onClick={() => onMarkComplete(reminder._id || reminder.id)} 
                        title="Mark Complete"
                      >
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {onDelete && (
                      <button 
                        className="action-icon" 
                        style={{ background: '#fee', color: '#ef4444' }}
                        onClick={() => onDelete(reminder._id || reminder.id)} 
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ReminderTable;
